import {
  createTreeRuntimeBranchLayoutAuthoritySnapshot,
  type TreeRuntimeBranchLayoutAuthoritySnapshot,
} from './TreeRuntimeBranchLayoutAuthoritySnapshot';

import {
  TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA,
  createTreeRuntimeCanonicalBranchCounterfactualShadowPlan,
  isTreeRuntimeCanonicalBranchCounterfactualShadowPlanInternallyConsistent,
  type TreeRuntimeCanonicalBranchCounterfactualComparison,
  type TreeRuntimeCanonicalBranchCounterfactualShadowPlan,
} from './TreeRuntimeCanonicalBranchCounterfactualShadowProjection';

export const
TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_MATRIX_SCHEMA =
  'CC-TREE-LIVE-003G3E4' as const;

export type TreeRuntimeBranchLayoutInteractionMatrixCellId =
  | 'A'
  | 'B'
  | 'C'
  | 'D';

export interface TreeRuntimeBranchLayoutInteractionLayoutSubstitution {
  readonly memberId: string;
  readonly canonicalLayoutPath: string;
}

export const
TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_MATRIX_CELLS =
  Object.freeze([
    Object.freeze({
      cell: 'A' as const,
      semanticBranch: 'PRODUCTION' as const,
      layoutPath: 'PRODUCTION' as const,
      role: 'CONTROL' as const,
    }),
    Object.freeze({
      cell: 'B' as const,
      semanticBranch: 'CANONICAL_SHADOW' as const,
      layoutPath: 'PRODUCTION' as const,
      role: '003G3E3_REFERENCE' as const,
    }),
    Object.freeze({
      cell: 'C' as const,
      semanticBranch: 'PRODUCTION' as const,
      layoutPath: 'CANONICAL_COMPATIBLE_SHADOW' as const,
      role: 'LAYOUT_ONLY_INTERVENTION' as const,
    }),
    Object.freeze({
      cell: 'D' as const,
      semanticBranch: 'CANONICAL_SHADOW' as const,
      layoutPath: 'CANONICAL_COMPATIBLE_SHADOW' as const,
      role: 'COMBINED_INTERVENTION' as const,
    }),
  ]);

export interface TreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan {
  readonly schema:
    typeof TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_MATRIX_SCHEMA;

  readonly productionBranchSource:
    TreeRuntimeBranchLayoutAuthoritySnapshot['branch']['source'];

  readonly productionLayoutSource:
    TreeRuntimeBranchLayoutAuthoritySnapshot['layoutPath']['source'];

  readonly productionBranchMigrationReadiness:
    TreeRuntimeBranchLayoutAuthoritySnapshot['branch']['migrationReadiness'];

  readonly productionLayoutReadiness:
    TreeRuntimeBranchLayoutAuthoritySnapshot['layoutPath']['readiness'];

  readonly semanticPlanCompatible: boolean;
  readonly semanticSubstitutedMemberCount: number;

  readonly layoutCandidateCount: number;
  readonly layoutSubstitutedMemberCount: number;
  readonly layoutParityMemberCount: number;
  readonly layoutMissingMemberCount: number;
  readonly layoutInvalidMemberCount: number;
  readonly layoutIndeterminateMemberCount: number;

  readonly layoutSubstitutions:
    readonly TreeRuntimeBranchLayoutInteractionLayoutSubstitution[];

  readonly layoutParityMemberIds:
    readonly string[];

  readonly layoutMissingMemberIds:
    readonly string[];

  readonly layoutInvalidMemberIds:
    readonly string[];

  readonly layoutIndeterminateMemberIds:
    readonly string[];

  readonly productionBranchSelectionHeld: true;
  readonly productionLayoutSelectionHeld: true;
  readonly productionReadinessOverrideAllowed: false;

  readonly sourceMutationAllowed: false;
  readonly causalAttributionAllowed: false;
  readonly legacyRetirementAuthorized: false;
  readonly productionAuthorityChanged: false;
  readonly privateIdentityPublished: false;
  readonly readOnly: true;
  readonly repairExecuted: false;
}

const INTEGER_TOKEN =
  /^(?:0|[1-9][0-9]*)$/;

export const
isCanonicalCompatibleTreeLayoutPath =
(
  path: string,
): boolean => {
  if (
    path.length === 0 ||
    path !== path.trim() ||
    /\s/.test(path)
  ) {
    return false;
  }

  const tokens =
    path.split('.');

  if (
    tokens.some(
      token =>
        token.length === 0,
    )
  ) {
    return false;
  }

  let index = 0;

  if (tokens[0] === 'self') {
    index = 1;
  } else if (tokens[0] === 'ancestor') {
    index = 1;

    if (
      index >= tokens.length ||
      !INTEGER_TOKEN.test(
        tokens[index],
      )
    ) {
      return false;
    }

    while (
      index < tokens.length &&
      INTEGER_TOKEN.test(
        tokens[index],
      )
    ) {
      index += 1;
    }
  } else {
    return false;
  }

  let partnerSeen = false;

  while (index < tokens.length) {
    const token =
      tokens[index];

    if (
      token === 'child' &&
      !partnerSeen
    ) {
      const childIndex =
        tokens[index + 1];

      if (
        childIndex === undefined ||
        !INTEGER_TOKEN.test(
          childIndex,
        )
      ) {
        return false;
      }

      index += 2;
      continue;
    }

    if (token === 'partner') {
      partnerSeen = true;
      index += 1;
      continue;
    }

    return false;
  }

  return true;
};

const freezeStrings = (
  values: readonly string[],
): readonly string[] =>
  Object.freeze(
    [...values],
  );

const freezeSubstitutions = (
  values:
    readonly TreeRuntimeBranchLayoutInteractionLayoutSubstitution[],
): readonly TreeRuntimeBranchLayoutInteractionLayoutSubstitution[] =>
  Object.freeze(
    values.map(
      value =>
        Object.freeze({
          ...value,
        }),
    ),
  );

export const
createTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan =
(
  input: {
    readonly authority:
      TreeRuntimeBranchLayoutAuthoritySnapshot;

    readonly semanticPlan:
      Pick<
        TreeRuntimeCanonicalBranchCounterfactualShadowPlan,
        'schema' | 'substitutedMemberCount'
      >;
  },
): TreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan => {
  const control =
    input.authority
      .layoutPath
      .branchPathByMemberId;

  const canonical =
    input.authority
      .layoutPath
      .canonicalCompatiblePathByMemberId;

  const candidates =
    Array.from(
      new Set([
        ...control.keys(),
        ...canonical.keys(),
      ]),
    ).sort();

  const substitutions:
    TreeRuntimeBranchLayoutInteractionLayoutSubstitution[] =
      [];

  const parity: string[] = [];
  const missing: string[] = [];
  const invalid: string[] = [];

  for (const memberId of candidates) {
    const canonicalPath =
      canonical.get(
        memberId,
      );

    if (canonicalPath === undefined) {
      missing.push(memberId);
      continue;
    }

    if (
      !isCanonicalCompatibleTreeLayoutPath(
        canonicalPath,
      )
    ) {
      invalid.push(memberId);
      continue;
    }

    const controlPath =
      control.get(
        memberId,
      );

    if (
      controlPath === canonicalPath
    ) {
      parity.push(memberId);
      continue;
    }

    substitutions.push({
      memberId,
      canonicalLayoutPath:
        canonicalPath,
    });
  }

  const indeterminate =
    [...missing, ...invalid]
      .sort();

  return Object.freeze({
    schema:
      TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_MATRIX_SCHEMA,

    productionBranchSource:
      input.authority.branch.source,

    productionLayoutSource:
      input.authority.layoutPath.source,

    productionBranchMigrationReadiness:
      input.authority
        .branch
        .migrationReadiness,

    productionLayoutReadiness:
      input.authority
        .layoutPath
        .readiness,

    semanticPlanCompatible:
      input.semanticPlan.schema ===
      TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA,

    semanticSubstitutedMemberCount:
      input.semanticPlan
        .substitutedMemberCount,

    layoutCandidateCount:
      candidates.length,

    layoutSubstitutedMemberCount:
      substitutions.length,

    layoutParityMemberCount:
      parity.length,

    layoutMissingMemberCount:
      missing.length,

    layoutInvalidMemberCount:
      invalid.length,

    layoutIndeterminateMemberCount:
      indeterminate.length,

    layoutSubstitutions:
      freezeSubstitutions(
        substitutions,
      ),

    layoutParityMemberIds:
      freezeStrings(parity),

    layoutMissingMemberIds:
      freezeStrings(missing),

    layoutInvalidMemberIds:
      freezeStrings(invalid),

    layoutIndeterminateMemberIds:
      freezeStrings(
        indeterminate,
      ),

    productionBranchSelectionHeld:
      true,

    productionLayoutSelectionHeld:
      true,

    productionReadinessOverrideAllowed:
      false,

    sourceMutationAllowed:
      false,

    causalAttributionAllowed:
      false,

    legacyRetirementAuthorized:
      false,

    productionAuthorityChanged:
      false,

    privateIdentityPublished:
      false,

    readOnly:
      true,

    repairExecuted:
      false,
  });
};

const uniqueCount = (
  values: readonly string[],
): number =>
  new Set(values).size;

const intersects = (
  left: ReadonlySet<string>,
  right: ReadonlySet<string>,
): boolean =>
  [...left].some(
    value =>
      right.has(value),
  );

export const
isTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlanInternallyConsistent =
(
  plan:
    TreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan,
): boolean => {
  const substitutedIds =
    plan.layoutSubstitutions.map(
      value =>
        value.memberId,
    );

  const substituted =
    new Set(substitutedIds);

  const parity =
    new Set(
      plan.layoutParityMemberIds,
    );

  const missing =
    new Set(
      plan.layoutMissingMemberIds,
    );

  const invalid =
    new Set(
      plan.layoutInvalidMemberIds,
    );

  const indeterminate =
    new Set(
      plan.layoutIndeterminateMemberIds,
    );

  return (
    plan.schema ===
      TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_MATRIX_SCHEMA &&

    plan.semanticPlanCompatible ===
      true &&

    Number.isInteger(
      plan.semanticSubstitutedMemberCount,
    ) &&
    plan.semanticSubstitutedMemberCount >=
      0 &&

    plan.layoutCandidateCount ===
      plan.layoutSubstitutedMemberCount +
      plan.layoutParityMemberCount +
      plan.layoutIndeterminateMemberCount &&

    plan.layoutSubstitutedMemberCount ===
      plan.layoutSubstitutions.length &&

    plan.layoutParityMemberCount ===
      plan.layoutParityMemberIds.length &&

    plan.layoutMissingMemberCount ===
      plan.layoutMissingMemberIds.length &&

    plan.layoutInvalidMemberCount ===
      plan.layoutInvalidMemberIds.length &&

    plan.layoutIndeterminateMemberCount ===
      plan.layoutIndeterminateMemberIds.length &&

    plan.layoutIndeterminateMemberCount ===
      plan.layoutMissingMemberCount +
      plan.layoutInvalidMemberCount &&

    uniqueCount(substitutedIds) ===
      substitutedIds.length &&

    uniqueCount(
      plan.layoutParityMemberIds,
    ) ===
      plan.layoutParityMemberIds.length &&

    uniqueCount(
      plan.layoutMissingMemberIds,
    ) ===
      plan.layoutMissingMemberIds.length &&

    uniqueCount(
      plan.layoutInvalidMemberIds,
    ) ===
      plan.layoutInvalidMemberIds.length &&

    uniqueCount(
      plan.layoutIndeterminateMemberIds,
    ) ===
      plan.layoutIndeterminateMemberIds.length &&

    plan.layoutSubstitutions.every(
      value =>
        isCanonicalCompatibleTreeLayoutPath(
          value.canonicalLayoutPath,
        ),
    ) &&

    !intersects(
      substituted,
      parity,
    ) &&

    !intersects(
      substituted,
      indeterminate,
    ) &&

    !intersects(
      parity,
      indeterminate,
    ) &&

    !intersects(
      missing,
      invalid,
    ) &&

    missing.size +
      invalid.size ===
      indeterminate.size &&

    [...missing].every(
      value =>
        indeterminate.has(value),
    ) &&

    [...invalid].every(
      value =>
        indeterminate.has(value),
    ) &&

    plan.productionBranchSelectionHeld ===
      true &&

    plan.productionLayoutSelectionHeld ===
      true &&

    plan.productionReadinessOverrideAllowed ===
      false &&

    plan.sourceMutationAllowed ===
      false &&

    plan.causalAttributionAllowed ===
      false &&

    plan.legacyRetirementAuthorized ===
      false &&

    plan.productionAuthorityChanged ===
      false &&

    plan.privateIdentityPublished ===
      false &&

    plan.readOnly ===
      true &&

    plan.repairExecuted ===
      false
  );
};


export const
createTreeRuntimeBranchLayoutInteractionShadowAuthority =
(
  authority:
    TreeRuntimeBranchLayoutAuthoritySnapshot,

  plan:
    TreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan,
): TreeRuntimeBranchLayoutAuthoritySnapshot => {
  if (
    !isTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlanAuthorityCoherent(
      plan,
      authority,
    )
  ) {
    throw new Error(
      'Invalid or stale 003G3E4 matrix plan.',
    );
  }

  const shadowLayoutPath =
    new Map(
      authority
        .layoutPath
        .branchPathByMemberId,
    );

  for (
    const substitution
    of plan.layoutSubstitutions
  ) {
    shadowLayoutPath.set(
      substitution.memberId,
      substitution.canonicalLayoutPath,
    );
  }

  return createTreeRuntimeBranchLayoutAuthoritySnapshot({
    viewerMemberId:
      authority.viewerMemberId,

    branch:
      authority.branch,

    layoutPath: {
      ...authority.layoutPath,

      branchPathByMemberId:
        shadowLayoutPath,

      /*
       * Deliberately preserve production selector metadata.
       *
       * Shadow consumption of the canonical-compatible map does not
       * manufacture production readiness or selector authority.
       */
      source:
        authority.layoutPath.source,

      readiness:
        authority.layoutPath.readiness,
    },
  });
};

export const
isTreeRuntimeBranchLayoutInteractionCounterfactualExecutionEligible =
(
  input: {
    readonly authenticatedSession:
      boolean;

    readonly plan:
      TreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan;

    readonly semanticComparison:
      TreeRuntimeCanonicalBranchCounterfactualComparison | null;
  },
): boolean => {
  const comparison =
    input.semanticComparison;

  if (!comparison) {
    return false;
  }

  const semanticOnlyCompleteRepair =
    comparison.availability ===
      'OBSERVED' &&
    comparison.controlParity ===
      true &&
    comparison.shadowFatalCount ===
      0 &&
    comparison.introducedFatalCount ===
      0;

  return (
    input.authenticatedSession ===
      true &&

    isTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlanInternallyConsistent(
      input.plan,
    ) &&

    input.plan.productionBranchSource ===
      'legacy-fallback' &&

    input.plan.productionLayoutSource ===
      'legacy-fallback' &&

    input.plan.semanticSubstitutedMemberCount >
      0 &&

    input.plan.layoutSubstitutedMemberCount >
      0 &&

    comparison.availability ===
      'OBSERVED' &&

    comparison.controlParity ===
      true &&

    comparison.controlWorldParity ===
      true &&

    comparison.controlFailureParity ===
      true &&

    comparison.invalidJoinCount ===
      0 &&

    comparison.controlFatalCount >
      0 &&

    !semanticOnlyCompleteRepair
  );
};

const sameStringArray = (
  left: readonly string[],
  right: readonly string[],
): boolean =>
  JSON.stringify(left) ===
  JSON.stringify(right);

const sameSubstitutionArray = (
  left:
    readonly TreeRuntimeBranchLayoutInteractionLayoutSubstitution[],
  right:
    readonly TreeRuntimeBranchLayoutInteractionLayoutSubstitution[],
): boolean =>
  JSON.stringify(left) ===
  JSON.stringify(right);

export const
isTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlanAuthorityCoherent =
(
  plan:
    TreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan,

  authority:
    TreeRuntimeBranchLayoutAuthoritySnapshot,
): boolean => {
  if (
    !isTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlanInternallyConsistent(
      plan,
    )
  ) {
    return false;
  }

  const expected =
    createTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan({
      authority,

      semanticPlan: {
        schema:
          TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA,

        substitutedMemberCount:
          plan.semanticSubstitutedMemberCount,
      },
    });

  return (
    plan.productionBranchSource ===
      authority.branch.source &&

    plan.productionLayoutSource ===
      authority.layoutPath.source &&

    plan.productionBranchMigrationReadiness ===
      authority.branch.migrationReadiness &&

    plan.productionLayoutReadiness ===
      authority.layoutPath.readiness &&

    plan.layoutCandidateCount ===
      expected.layoutCandidateCount &&

    plan.layoutSubstitutedMemberCount ===
      expected.layoutSubstitutedMemberCount &&

    plan.layoutParityMemberCount ===
      expected.layoutParityMemberCount &&

    plan.layoutMissingMemberCount ===
      expected.layoutMissingMemberCount &&

    plan.layoutInvalidMemberCount ===
      expected.layoutInvalidMemberCount &&

    plan.layoutIndeterminateMemberCount ===
      expected.layoutIndeterminateMemberCount &&

    sameSubstitutionArray(
      plan.layoutSubstitutions,
      expected.layoutSubstitutions,
    ) &&

    sameStringArray(
      plan.layoutParityMemberIds,
      expected.layoutParityMemberIds,
    ) &&

    sameStringArray(
      plan.layoutMissingMemberIds,
      expected.layoutMissingMemberIds,
    ) &&

    sameStringArray(
      plan.layoutInvalidMemberIds,
      expected.layoutInvalidMemberIds,
    ) &&

    sameStringArray(
      plan.layoutIndeterminateMemberIds,
      expected.layoutIndeterminateMemberIds,
    )
  );
};


const sortedSetValues = (
  values: ReadonlySet<string>,
): readonly string[] =>
  [...values].sort();

const sortedMapEntries = (
  values: ReadonlyMap<string, string>,
): readonly (readonly [string, string])[] =>
  [...values.entries()]
    .sort(
      (left, right) =>
        left[0].localeCompare(
          right[0],
        ),
    );

export const
isTreeRuntimeCanonicalBranchCounterfactualShadowPlanAuthorityCoherent =
(
  plan:
    TreeRuntimeCanonicalBranchCounterfactualShadowPlan,

  authority:
    TreeRuntimeBranchLayoutAuthoritySnapshot,
): boolean => {
  if (
    !isTreeRuntimeCanonicalBranchCounterfactualShadowPlanInternallyConsistent(
      plan,
    )
  ) {
    return false;
  }

  const expected =
    createTreeRuntimeCanonicalBranchCounterfactualShadowPlan(
      authority.branch,
    );

  return (
    plan.productionSource ===
      authority.branch.source &&

    plan.productionMigrationReadiness ===
      authority.branch.migrationReadiness &&

    plan.candidateMemberCount ===
      expected.candidateMemberCount &&

    plan.substitutedMemberCount ===
      expected.substitutedMemberCount &&

    plan.indeterminateMemberCount ===
      expected.indeterminateMemberCount &&

    plan.canonicalMissingMemberCount ===
      expected.canonicalMissingMemberCount &&

    plan.canonicalUnresolvedMemberCount ===
      expected.canonicalUnresolvedMemberCount &&

    plan.canonicalInvalidMemberCount ===
      expected.canonicalInvalidMemberCount &&

    JSON.stringify(
      sortedMapEntries(
        plan.canonicalSemanticBranchPathByMemberId,
      ),
    ) ===
      JSON.stringify(
        sortedMapEntries(
          expected.canonicalSemanticBranchPathByMemberId,
        ),
      ) &&

    JSON.stringify(
      sortedSetValues(
        plan.substitutedMemberIds,
      ),
    ) ===
      JSON.stringify(
        sortedSetValues(
          expected.substitutedMemberIds,
        ),
      ) &&

    JSON.stringify(
      sortedSetValues(
        plan.indeterminateMemberIds,
      ),
    ) ===
      JSON.stringify(
        sortedSetValues(
          expected.indeterminateMemberIds,
        ),
      )
  );
};
